// Cluster ZUWEISEN (C9) — reine Logik für „diese Nummer haben wir noch nicht".
//
// Bisher gab es für einen erkannten Kontakt nur den Nachtrag der ABSENDERADRESSE
// an genau diesen Kontakt. Eine in der Signatur gefundene Durchwahl landete
// damit blind dort, wo der Erkennungs-Chip gerade hinzeigte — häufig an der
// Firma, obwohl sie dem Ansprechpartner gehört (oder umgekehrt).
//
// C9 macht daraus eine eingeschränkte Auswahl:
//   · Firmenbezug  → die Firma, ein bestehender Ansprechpartner, oder ein neuer.
//   · Privatkontakt → nur die Frage privat oder geschäftlich.

import { AP_NEW_PERSON_KEY, type KommArt } from "./mailContactAssign";
import type { MenschItem } from "../verantwortlichkeit/buildMenschenItems";

/**
 * Annahme A3: C9 gilt auch für fehlende E-MAIL-Adressen, nicht nur für Nummern.
 * Die Erkennung meldet beides, die Zielauswahl ist identisch, und eine
 * unbekannte Adresse ist dieselbe fachliche Lücke. Abschalten wäre eine Zeile.
 */
export const C9_ARTEN = ["phone", "mobile", "fax", "email"] as const;
export type C9Art = (typeof C9_ARTEN)[number];

/** Ein gemeldeter fehlender Kontaktdatensatz (Spiegel von `MailMissingContactData.entries`). */
export type FehlenderEintrag = {
    kind: C9Art;
    value: string;
    display: string;
    source: "sender" | "signature";
    confident: boolean;
};

/** Ein wählbares Ziel der Übernahme. */
export type NummerZiel =
    | { art: "firma"; contactId: number; label: string }
    | { art: "ap"; contactId: number; label: string; firmaId: number }
    | { art: "ap-neu"; firmaId: number; label: string }
    | { art: "privat"; contactId: number; label: string };

/** Der Kontaktbezug, aus dem die Zielliste entsteht. */
export type NummerBezug = {
    /** Der erkannte/zugewiesene Kontakt. */
    kontakt: { id: number; displayName: string } | null;
    /** Firma des Kontakts — gesetzt, wenn er selbst eine Firma ist ODER ihr AP. */
    firma: { id: number; displayName: string } | null;
};

/** Bestehende Ansprechpartner einer Firma (aus GET /ansprechpartner-cleanup/firma/:id). */
export type FirmaPerson = { personId: number; lastname: string | null; firstName: string | null };

const personLabel = (p: FirmaPerson): string => {
    const l = (p.lastname ?? "").trim();
    const f = (p.firstName ?? "").trim();
    return f ? `${l}, ${f}` : l || `#${p.personId}`;
};

/**
 * Die wählbaren Ziele für eine gefundene Nummer/Adresse.
 *
 * Mit Firmenbezug: die Firma, jeder bestehende Ansprechpartner (alphabetisch)
 * und „Neuer Ansprechpartner der Firma". Ohne Firmenbezug bleibt nur der
 * Kontakt selbst — dort entscheidet allein privat/geschäftlich.
 * Ohne Kontakt gibt es nichts zu tun: leere Liste.
 */
export function zieleFuerNummer(bezug: NummerBezug, firmaPersonen: FirmaPerson[]): NummerZiel[] {
    if (!bezug.kontakt) return [];
    if (!bezug.firma) {
        return [{ art: "privat", contactId: bezug.kontakt.id, label: bezug.kontakt.displayName }];
    }
    const firmaId = bezug.firma.id;
    const ziele: NummerZiel[] = [
        { art: "firma", contactId: firmaId, label: bezug.firma.displayName },
    ];
    for (const p of [...firmaPersonen].sort((a, b) => personLabel(a).localeCompare(personLabel(b), "de"))) {
        ziele.push({ art: "ap", contactId: p.personId, label: personLabel(p), firmaId });
    }
    ziele.push({ art: "ap-neu", firmaId, label: "Neuer Ansprechpartner der Firma" });
    return ziele;
}

/** Vorauswahl: der erkannte Kontakt selbst, sonst das erste Ziel. */
export function standardZiel(ziele: NummerZiel[], bezug: NummerBezug): NummerZiel | null {
    if (ziele.length === 0) return null;
    const eigenes = ziele.find(
        (z) => z.art !== "ap-neu" && z.contactId === bezug.kontakt?.id,
    );
    return eigenes ?? ziele[0];
}

export type KommPayload = {
    type: C9Art;
    value: string;
    label?: string;
    kind?: KommArt;
    companyId?: number;
    isDefault: boolean;
};

/**
 * Der Payload für `POST /contact/:id/communication` — plus die Kontakt-id, an
 * die er geht. `null` bei „neuer Ansprechpartner": der läuft über
 * `buildNeuerApItem`, weil dort erst ein Kontakt entstehen muss.
 *
 * Wichtig (assertCommunicationKindCompany): an einer FIRMA darf weder `kind`
 * noch `companyId` stehen, sonst antwortet der Server mit 400. `companyId` gibt
 * es nur an einer Person mit `kind='geschaeftlich'`.
 */
export function buildNummerPayload(
    ziel: NummerZiel,
    eintrag: { kind: C9Art; value: string },
    optionen: { art?: KommArt; hatDefault?: boolean } = {},
): { contactId: number; payload: KommPayload } | null {
    const isDefault = !optionen.hatDefault;
    if (ziel.art === "firma") {
        // Firmen tragen kein kind — der Server nullt es und lehnt companyId ab.
        return { contactId: ziel.contactId, payload: { type: eintrag.kind, value: eintrag.value, isDefault } };
    }
    if (ziel.art === "ap") {
        return {
            contactId: ziel.contactId,
            payload: {
                type: eintrag.kind,
                value: eintrag.value,
                label: "Geschäftlich",
                kind: "geschaeftlich",
                companyId: ziel.firmaId,
                isDefault,
            },
        };
    }
    if (ziel.art === "privat") {
        const art: KommArt = optionen.art ?? "privat";
        return {
            contactId: ziel.contactId,
            payload: {
                type: eintrag.kind,
                value: eintrag.value,
                label: art === "geschaeftlich" ? "Geschäftlich" : "Privat",
                kind: art,
                isDefault,
            },
        };
    }
    return null;
}

/**
 * „Neuer Ansprechpartner der Firma": EIN MenschItem für attach-menschen. Die
 * geschäftliche Zeile bekommt ihre `companyId` durch die Backend-Ergänzung zu
 * C8 (`buildNewContactComms`) — hier muss sie nicht mitgegeben werden.
 */
export function buildNeuerApItem(
    firmaId: number,
    person: { lastName: string; firstName?: string },
    eintrag: { kind: C9Art; value: string },
): MenschItem {
    const wert = eintrag.value.trim();
    return {
        relationId: 0,
        personId: 0,
        companyId: firmaId,
        isMitarbeiterBav: false,
        isNew: true,
        role: "mensch",
        verantwortlichkeit: "",
        newPersonKey: AP_NEW_PERSON_KEY,
        newPerson: {
            lastName: person.lastName.trim(),
            firstName: (person.firstName ?? "").trim() || undefined,
            ...(eintrag.kind === "phone" ? { phoneWork: wert } : {}),
            ...(eintrag.kind === "mobile" ? { mobileWork: wert } : {}),
            ...(eintrag.kind === "email" ? { emailWork: wert } : {}),
            // Fax kennt attach-menschen nicht — die Nummer wird nach dem Anlegen
            // über POST /contact/:id/communication nachgetragen.
        },
    };
}

// ── „Später" — pro Browser-Sitzung merken (Annahme A4) ───────────────────────
// Bewusst `sessionStorage` und kein Backend-Feld: Ein „nie wieder" ohne
// Verwaltungsoberfläche wäre schlechter als gar keins. Die Wirkung hält genau
// so lange wie die Arbeitssitzung.

const spaeterKey = (mailbox: string, messageId: string) =>
    `kmvs.mail.nummerSpaeter.${mailbox}.${messageId}`;

/** Normalisiert einen Wert für den Vergleich (Nummernformate, Groß/Klein). */
export function normalisiereWert(value: string): string {
    return (value ?? "").toLowerCase().replace(/[\s()/.-]/g, "");
}

export function ladeSpaeter(mailbox: string, messageId: string): string[] {
    try {
        const raw = sessionStorage.getItem(spaeterKey(mailbox, messageId));
        const list = raw ? JSON.parse(raw) : [];
        return Array.isArray(list) ? list.map(String) : [];
    } catch {
        return [];
    }
}

export function merkeSpaeter(mailbox: string, messageId: string, werte: string[]): void {
    try {
        const vorhanden = new Set(ladeSpaeter(mailbox, messageId));
        for (const w of werte) vorhanden.add(normalisiereWert(w));
        sessionStorage.setItem(spaeterKey(mailbox, messageId), JSON.stringify([...vorhanden]));
    } catch {
        // Privater Modus / abgeschaltete Storage: dann wird eben wieder gefragt.
    }
}

/** Die Einträge, die in dieser Sitzung noch nicht zurückgestellt wurden. */
export function offeneEintraege(
    eintraege: FehlenderEintrag[],
    zurueckgestellt: readonly string[],
): FehlenderEintrag[] {
    const raus = new Set(zurueckgestellt.map(normalisiereWert));
    return eintraege.filter(
        (e) => C9_ARTEN.includes(e.kind) && !raus.has(normalisiereWert(e.value)),
    );
}
